import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useUserStore } from "@/store/userStore";
import { useLocalDbStore } from "@/store/localDbStore";
import {
  getDashboardStats,
  getRecentScans,
  isRequestCancelled,
  type DashboardStats,
  type RecentScan,
} from "@/services/dashboard.service";
import {
  Camera,
  BookOpen,
  FileText,
  BarChart3,
  Clock,
  CheckCircle2,
  Sparkles,
  Users,
  Check,
} from "lucide-react";
import { BrandName } from "@/components/brand/BrandLogo";

export default function Dashboard() {
  const profile = useUserStore((s) => s.profile);
  const credits = useUserStore((s) => s.credits);
  const isDbConnected = useLocalDbStore((s) => s.isConnected);
  const students = useLocalDbStore((s) => s.students);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);
  const [loading, setLoading] = useState(true);

  // Get trial data from credits store
  const trialDaysLeft = credits?.daysRemaining ?? 14;
  const isOnTrial = credits?.subscriptionStatus === "trial";

  // Get display name from profile (stored by OnboardingCheck)
  const displayName = useMemo(() => {
    if (!profile) return null;
    if (profile.displayName) return profile.displayName.split(" ")[0];
    if (profile.email) return profile.email.split("@")[0];
    return null;
  }, [profile]);

  // Track completion status for Getting Started
  const hasStudents = isDbConnected && students.length > 0;
  const hasScans = stats && stats.totalScans > 0;
  const hasMultipleScans = stats && stats.totalScans >= 3;

  // Show getting started until user has students AND at least 3 scans
  const showGettingStarted = useMemo(() => {
    return !hasStudents || !hasMultipleScans;
  }, [hasStudents, hasMultipleScans]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsData, scansData] = await Promise.all([
          getDashboardStats(controller.signal),
          getRecentScans(5, controller.signal),
        ]);

        if (!controller.signal.aborted) {
          setStats(statsData);
          setRecentScans(scansData);
        }
      } catch (error) {
        if (!isRequestCancelled(error)) {
          console.error("Failed to load dashboard data:", error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Welcome Section */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          {displayName ? `Welcome back, ${displayName}!` : <>Welcome to <BrandName />!</>}
        </h1>
        <p className="text-muted-foreground">
          Grade worksheets instantly with AI-powered scanning.
        </p>
      </div>

      {/* Trial Banner */}
      {isOnTrial && (
        <Alert className="bg-primary/5 border-primary/20">
          <Sparkles className="h-4 w-4 text-primary" />
          <AlertTitle className="text-primary">Free Trial</AlertTitle>
          <AlertDescription>
            You have {trialDaysLeft} days left in your free trial.{" "}
            <Link to="/settings/billing" className="font-medium underline underline-offset-4">
              Upgrade now
            </Link>{" "}
            to keep grading after your trial ends.
          </AlertDescription>
        </Alert>
      )}

      {/* Quick Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button asChild size="lg" className="flex-1 sm:flex-none">
          <Link to="/scan">
            <Camera className="mr-2 h-5 w-5" />
            Scan Worksheet
          </Link>
        </Button>
        <Button asChild variant="outline" size="lg" className="flex-1 sm:flex-none">
          <Link to="/worksheets">
            <BookOpen className="mr-2 h-5 w-5" />
            Browse Worksheets
          </Link>
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Scans</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.totalScans ?? 0}</div>
                <CardDescription>Worksheets graded</CardDescription>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Score</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {stats?.averageScore != null ? `${Math.round(stats.averageScore)}%` : "--"}
                </div>
                <CardDescription>
                  {stats?.averageScore !== null ? "Across all scans" : "No data yet"}
                </CardDescription>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Worksheets Used</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.worksheetsUsed ?? 0}</div>
                <CardDescription>Unique worksheets</CardDescription>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.scansThisWeek ?? 0}</div>
                <CardDescription>Scans this week</CardDescription>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Scans */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Scans
            </CardTitle>
            <CardDescription>Your latest graded worksheets</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10 rounded" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-6 w-12" />
                  </div>
                ))}
              </div>
            ) : recentScans.length > 0 ? (
              <div className="space-y-3">
                {recentScans.map((scan) => (
                  <Link
                    key={scan.id}
                    to={`/scans/${scan.id}`}
                    className="flex items-center gap-4 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{scan.worksheetName}</p>
                      <p className="text-sm text-muted-foreground">
                        {scan.studentName ?? "Unknown student"} &middot;{" "}
                        {new Date(scan.scannedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-sm font-medium">
                      {scan.score}/{scan.totalQuestions}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground mb-4">No scans yet. Scan your first worksheet!</p>
                <Button asChild variant="outline" size="sm">
                  <Link to="/scan">
                    <Camera className="mr-2 h-4 w-4" />
                    Start Scanning
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Getting Started Guide */}
        {showGettingStarted && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Getting Started
              </CardTitle>
              <CardDescription>Follow these steps to start grading worksheets</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Step 1: Add Students */}
                <Link
                  to="/students"
                  className="flex gap-4 p-2 -m-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    hasStudents
                      ? "bg-green-500 text-white"
                      : "bg-primary text-primary-foreground"
                  }`}>
                    {hasStudents ? <Check className="h-4 w-4" /> : "1"}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${hasStudents ? "text-muted-foreground" : ""}`}>
                      Add your students
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {hasStudents
                        ? `${students.length} student${students.length !== 1 ? "s" : ""} added`
                        : "Create a class and add student names with sheet numbers"}
                    </p>
                  </div>
                  {!hasStudents && <Users className="h-5 w-5 text-muted-foreground self-center" />}
                </Link>

                {/* Step 2: Browse Worksheets */}
                <Link
                  to="/worksheets"
                  className="flex gap-4 p-2 -m-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Browse worksheets</p>
                    <p className="text-sm text-muted-foreground">
                      Find worksheets that match your curriculum
                    </p>
                  </div>
                  <BookOpen className="h-5 w-5 text-muted-foreground self-center" />
                </Link>

                {/* Step 3: Print */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                    3
                  </div>
                  <div>
                    <p className="font-medium">Print and distribute</p>
                    <p className="text-sm text-muted-foreground">
                      Print copies for your students to complete
                    </p>
                  </div>
                </div>

                {/* Step 4: Scan */}
                <Link
                  to="/scan"
                  className="flex gap-4 p-2 -m-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    hasScans
                      ? "bg-green-500 text-white"
                      : "bg-primary text-primary-foreground"
                  }`}>
                    {hasScans ? <Check className="h-4 w-4" /> : "4"}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${hasScans ? "text-muted-foreground" : ""}`}>
                      Scan completed work
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {hasScans
                        ? `${stats?.totalScans} worksheet${stats?.totalScans !== 1 ? "s" : ""} scanned`
                        : "Take a photo or upload the completed worksheet"}
                    </p>
                  </div>
                  {!hasScans && <Camera className="h-5 w-5 text-muted-foreground self-center" />}
                </Link>

                {/* Step 5: Review */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                    5
                  </div>
                  <div>
                    <p className="font-medium">Review grades</p>
                    <p className="text-sm text-muted-foreground">
                      AI grades automatically, you review and confirm
                    </p>
                  </div>
                </div>

                {/* Next Action Button */}
                <div className="pt-4">
                  {!hasStudents ? (
                    <Button asChild className="w-full">
                      <Link to="/students">
                        <Users className="mr-2 h-4 w-4" />
                        Add Your Students
                      </Link>
                    </Button>
                  ) : !hasScans ? (
                    <Button asChild className="w-full">
                      <Link to="/worksheets">
                        <BookOpen className="mr-2 h-4 w-4" />
                        Browse Worksheets
                      </Link>
                    </Button>
                  ) : (
                    <Button asChild className="w-full">
                      <Link to="/scan">
                        <Camera className="mr-2 h-4 w-4" />
                        Scan More Worksheets
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
